import { filterCategoryCleaner } from '@helpers/searchHelper'
import { FETCH_WITH_HEADERS } from '@services/react-query/config'
import { CustomAxiosRequestHeaders } from '@services/react-query/hooks/orders/useGetOrderHistory'
import { GET_ALL_COURSES_URL } from '@services/react-query/urls/courses'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ShortCourse } from '@type/course'
import { Filters } from '@type/main'

const useGetAllCourses = ({
  query,
  filters,
  sort,
  limit = 20,
  initialCourses,
  initialTotal,
}: {
  query: string
  filters: Filters
  sort: Object
  limit: number
  initialCourses: ShortCourse[]
  initialTotal: number
}) => {
  const rating = {
    min: 0,
    max: 0,
  }
  if (filters.rating) {
    rating.min = filters.rating
    rating.max = filters.rating + 0.99
  }
  const queryParams = new URLSearchParams({
    search: query.trim(),
    filters: JSON.stringify(
      filterCategoryCleaner({ ...filters, rating } as any)
    ),
    sort: JSON.stringify(sort),
    limit: limit.toString(),
  })

  const { data, ...others } = useInfiniteQuery<{
    data: ShortCourse[]
    headers: CustomAxiosRequestHeaders
  }>({
    queryKey: ['courses', queryParams.toString()],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await FETCH_WITH_HEADERS(
        `${GET_ALL_COURSES_URL(queryParams.toString())}&page=${pageParam}`
      )
      return response
    },
    getNextPageParam: (lastPage, pages) => {
      const total = parseInt(lastPage?.headers?.['x-total'] || '0', 10)
      const nextPage = pages.length + 1
      const lastPageNum = parseInt(
        lastPage?.headers?.['x-page'] || nextPage,
        10
      )
      const totalPages = Math.ceil(total / limit)

      return totalPages < lastPageNum ? undefined : nextPage
    },
    initialPageParam: 1,
    initialData: {
      pages: [
        {
          data: initialCourses,
          headers: {
            'x-total': initialTotal,
            'x-page': '1',
          } as unknown as CustomAxiosRequestHeaders,
        },
      ],
      pageParams: [1],
    },
  })

  const total = parseInt(data?.pages[0]?.headers?.['x-total'] || '0', 10)
  const courses = data?.pages.flatMap((page) => page.data) || []

  return { ...others, total, data: courses }
}

export default useGetAllCourses
